<template>
  <div id="app">
    <!-- Show header and scanner only on main views -->
    <template v-if="$route.name !== 'QRView' && $route.name !== 'Login'">
      <Header 
        :current-view="currentView" 
        @view-change="handleViewChange" 
        @open-scanner="handleOpenScanner" 
      />
      
      <MessageNotification 
        :message="message" 
        @close="handleMessageClose" 
      />
    </template>

    <!-- Main Content -->
    <router-view v-slot="{ Component }">
      <transition name="fade">
        <component 
          :is="Component" 
          @check-in="handleCheckIn"
          @qr-scan="handleQRScan"
        />
      </transition>
    </router-view>

    <!-- QR Scanner Modal - only show on main views -->
    <QRScanner 
      v-if="$route.name !== 'QRView' && $route.name !== 'Login'"
      :is-open="showScanModal" 
      :paused="scannerPaused"
      @close="showScanModal = false" 
      @scan-success="handleQRScan" 
    />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import Header from './components/Header.vue'
import MessageNotification from './components/MessageNotification.vue'
import GuestListView from './views/GuestListView.vue'
import QRScanner from './components/QRScanner.vue'
import { GuestService } from './services/guestService.js'
import AuthService from './services/authService.js'

export default {
  name: 'App',
  components: {
    Header,
    MessageNotification,
    GuestListView,
    QRScanner
  },
  setup() {
    const route = useRoute()
    const currentView = ref('list')
    const showScanModal = ref(false)
    const message = ref({
      show: false,
      type: 'success',
      text: '',
      guest: null
    })
    const guests = ref([])
    const scannerPaused = ref(false)
    let messageTimeout = null
    const guestService = new GuestService()

    const pauseScanner = () => {
      scannerPaused.value = true
    }

    const resumeScanner = () => {
      scannerPaused.value = false
    }

    const loadGuests = async () => {
      await guestService.loadGuests()
      guests.value = guestService.getGuests()
    }

    const showMessage = (type, text, guest = null) => {
      if (messageTimeout) {
        clearTimeout(messageTimeout)
      }

      message.value = {
        show: true,
        type,
        text,
        guest
      }
      
      // Auto hide after 5 seconds
      pauseScanner()
      messageTimeout = setTimeout(() => {
        message.value.show = false
        messageTimeout = null
        resumeScanner()
      }, 5000)
    }

    const handleMessageClose = () => {
      message.value.show = false
      if (messageTimeout) {
        clearTimeout(messageTimeout)
        messageTimeout = null
      }
      resumeScanner()
    }

    const handleViewChange = (view) => {
      currentView.value = view
    }

    const handleOpenScanner = () => {
      showScanModal.value = true
    }

    const handleCheckIn = async (guest) => {
      const result = await guestService.checkInGuest(guest)
      if (result.success) {
        showMessage('success', result.message, result.guest)
        guests.value = guestService.getGuests()
      } else {
        showMessage('error', result.message, result.guest)
      }
    }

    const handleQRScan = async (qrData) => {
      const result = await guestService.processQRCode(qrData)
      
      if (result.success) {
        showMessage('success', result.message, result.guest)
        guests.value = guestService.getGuests()
      } else {
        showMessage('error', result.message, result.guest)
      }
    }

    onMounted(() => {
      // Only load guests if authenticated and on home route
      if (AuthService.isAuthenticated() && route.name === 'Home') {
        loadGuests()
      }
    })

    return {
      route,
      currentView,
      showScanModal,
      message,
      guests,
      scannerPaused,
      handleViewChange,
      handleOpenScanner,
      handleCheckIn,
      handleQRScan,
      handleMessageClose
    }
  }
}
</script>
